import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Paper, Box, Typography, Tabs, Tab, TextField } from '@material-ui/core';
import { GridList, GridListTile } from '@material-ui/core';
import { Fab, Button, IconButton } from '@material-ui/core';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import PersonIcon from '@material-ui/icons/Person';
import AddIcon from '@material-ui/icons/Add';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DraggableEvent } from 'react-draggable';

import { RootState } from '../redux/rootReducer';
import { FolderDb, updateFolder, setCurrentFolder } from '../redux/folderDbSlice';
import { BookDb, setCurrentBook } from '../redux/bookDbSlice';
import { RelationFolderBookDb, updateRelationFolderBook } from '../redux/relationFolderBookDbSlice';
import { updateUserCondition } from '../redux/userConditionSlice';
import { defaultBookCoverImage } from '../redux/preparedImages';
import useImageData from '../hooks/useImageData';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useAllFoldersDb from '../hooks/useAllFoldersDb';
import useCurrentFolderId from '../hooks/useCurrentFolderId'
import useCurrentFolderDb from '../hooks/useCurrentFolderDb';
import useOneFolderDb from '../hooks/useOneFolderDb';
import useElementDimensions from '../hooks/useElementDimensions';
import useUserCondition from '../hooks/useUserCondition';
import useWindowSize from '@rehooks/window-size';

import { deepCopy, getTimestamp } from '../general';
import DraggableSort from '../general/DraggableSort';
import TabPanel, { TabsAppBar, a11yProps } from './TabPanel';
import PutTransition from '../general/PutTransition';
import AddBookDialog from './AddBookDialog';
import MemberInformationDialog from './MemberInformationDialog'
import RemoveMemberDialog from './RemoveMemberDialog';
import AddMemberDialog from './AddMemberDialog';
import ChangeOwnerDialog from './ChangeOwnerDialog';
import DeleteFolderDialog from './DeleteFolderDialog';
import DeleteBookDialog from './DeleteBookDialog';
import CopyBookDialog from './CopyBookDialog';
import PublishMenu, { PublishMenuAnchorType } from '../shop_components/PublishMenu';
import PublishDialog from '../shop_components/PublishDialog';

const FolderPaper = styled(Paper)`
  height: 100vh;
  overflow: hidden;
`;
const HedderPaper = styled(Paper)`
  height: 24px;
  width: 100%;
`;
type TileWrapperProps = {
  $dragging: boolean;
  otherDragging: boolean;
}
const TileWrapper = styled.div<TileWrapperProps>`
  position: absolute;
  width: min(16vw, 160px);
  text-align: center;
  pointer-events: ${props => props.$dragging ? 'none' : undefined};
  z-index: ${props => props.$dragging ? 2 : 1};
  transition: ${props => props.$dragging ? undefined : props.otherDragging ? '200ms ease-out' : undefined};
`;
const GridListTileCenter = styled(GridListTile)`
  text-align: center;
`;
const BookImage = styled.img`
  width: min(16vw, 160px);
  height: min(16vw, 160px);
`;
type BookButtonProps = {
  $dragging: boolean;
  $parentChanging: boolean;
};
const BookButton = styled(Button)<BookButtonProps>`
  color: transparent;
  width: min(16vw, 160px);
  height: min(16vw, 160px);
  opacity: ${props => props.$parentChanging ? '0.5' : undefined};
  transform: ${props => props.$parentChanging ? 'scale(0.7)' : undefined};
  transition: ${props => props.$dragging ? '100ms ease-in' : undefined};
`;
type EditMenuBoxProps = {
  $dragging: boolean;
}
const EditMenuBox = styled(Box)<EditMenuBoxProps>`
  opacity: ${props => props.$dragging ? 0 : 1};
  transition: opacity 300ms;
`;
const PersonIconGrey = styled(PersonIcon)`
  color: grey;
  font-size: min(16vw, 160px);
`;
const AddFab = styled(Fab)`
  position: absolute;
  bottom: 16px;
  right: 16px;
  color: white;
  background-color: #1e88e5;
  &:hover {
    background-color: #1976d2;
  }
`;
const ChangeOwnerBox = styled(Box)`
  height: 20vh;
  display: flex;
  justify-content: center;
  align-items: flex-end;
`;
const ChangeFolderNameBox = styled(Box)`
  height: 20vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

type BookValueType = BookDb & { order: number };
type BookListHashType = { [key in string]: { ids: string[], values: BookValueType[] } }
const listCols = 3;
const booksGridListId = 'books-grid-list';

type BooksGridListProps = {
  userId: string | undefined;
  ids: string[];
  values: BookValueType[];
  setValues: (values: BookValueType[]) => void;
  colWidth: number;
  setColWidth: (value: number) => void;
  draggingId?: string;
  setDraggingId: (id?: string) => void;
  parentChangingId?: string;
  changeBookOrder: (ev: DraggableEvent, id: string) => void;
  checkParentChanging: (ev: DraggableEvent, id: string) => void;
  updateOrder: (id: string) => void;
  transitionStart: (id: string, order: number) => void;
  newbornId?: string;
  setNewbornId: (id?: string) => void;
}
const BooksGridList: React.FC<BooksGridListProps> = ({userId, ids, values, setValues, colWidth, setColWidth, draggingId, setDraggingId, parentChangingId, changeBookOrder, checkParentChanging, updateOrder, transitionStart, newbornId, setNewbornId}) => {
  const dispatch = useDispatch()
  const [userConditionId] = useUserCondition(userId);
  const { width } = useElementDimensions('books-grid-list');
  const [deleteAction, setDeleteAction] = useState({show: false, bookId: ''})
  const [copyAction, setCopyAction] = useState({show: false, bookId: ''})
  const windowSize = useWindowSize();
  const verticalOffset = 64 + 24 + 48 + 16;
  const [menuAnchor, setMenuAnchor] = useState<PublishMenuAnchorType>();
  type DialogActionType = { show: boolean, id: string | undefined }
  const [publishAction, setPublishAction] = useState<DialogActionType>({ show: false, id: undefined });

  useEffect(() => {
    if (width) setColWidth(width / 3);
  }, [width])

  const editClicked = (id: string) => {
    const link = document.getElementById('link-to-book');
    if (!link) return;
    link.click();
    dispatch(setCurrentBook(id));
    if (userConditionId) dispatch(updateUserCondition({ id: userConditionId, bookTreeDisplay: true, where: 'book', when: getTimestamp(), bookId: id }));
  }
  const openPublishMenu = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string) => {
    setMenuAnchor({ open: true, anchorPosition: { left: ev.pageX, top: ev.pageY }, id });
  }

  return (
    <div id={booksGridListId} style={{paddingLeft: colWidth / 8, paddingTop: 16, position: 'relative', zIndex: 0, height: windowSize.innerHeight - verticalOffset, overflow: 'auto'}}>
      { ids.map((id, ix) => {
        const book = values[ix];
        return (
          <DraggableSort
            draggableProps={{
              position: { x: colWidth * (book.order % listCols), y: (colWidth + 40) * Math.trunc(book.order / listCols) },
              handle: '.handle-book',
              onStop: (ev: DraggableEvent) => changeBookOrder(ev, id),
              bounds: { left: undefined, top: undefined, right: undefined, bottom: undefined }
            }}
            targetClass="draggable-tile"
            onDrag={(ev: DraggableEvent) => checkParentChanging(ev, id)}
            key={id}
            id={id} ids={ids} values={values}
            setValues={setValues}
            setDraggingId={setDraggingId}
          >
            <TileWrapper id={id} className="draggable-tile"
              $dragging={id === draggingId} otherDragging={Boolean(draggingId) && id !== draggingId}
            >
              <PutTransition newborn={id === newbornId} done={() => setNewbornId()}>
                <BookButton className="handle-book" onDoubleClick={() => editClicked(id)}
                  $dragging={id === draggingId} $parentChanging={id === parentChangingId}
                  onContextMenu={ev => openPublishMenu(ev, id)}
                >
                  <RenderBookCover imageId={book.coverImage ? book.coverImage.imageId : undefined} />
                </BookButton>
                <EditMenuBox position="relative" left="5px" mt={-2} $dragging={id === draggingId}>
                  <IconButton onClick={() => setCopyAction({show: true, bookId: id})}>
                    <FileCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton onClick={() => editClicked(id)}>
                    <EditOutlinedIcon fontSize="small"/>
                  </IconButton>
                  <IconButton onClick={() => setDeleteAction({show: true, bookId: id})}>
                    <DeleteOutlinedIcon fontSize="small"/>
                  </IconButton>
                </EditMenuBox>
                <Typography>
                  { book.name }
                </Typography>
              </PutTransition>
            </TileWrapper>
          </DraggableSort>
        );
      }) }
      <Link to="/book" id="link-to-book" />
      <DeleteBookDialog open={deleteAction.show}
        onClose={() => setDeleteAction({show: false, bookId: ''})}
        bookId={deleteAction.bookId}
        updateOrder={updateOrder}
      />
      <CopyBookDialog open={copyAction.show}
        onClose={() => setCopyAction({show: false, bookId: ''})}
        bookId={copyAction.bookId}
        transitionStart={id => transitionStart(id, ids.length)}
      />
      <PublishMenu menuAnchor={menuAnchor}
        onClose={() => {
          setMenuAnchor(pre => {
            if (!pre) return undefined;
            return { ...pre, open: false };
          });
          setTimeout(() => setMenuAnchor(undefined), 280);
        }}
        functions={{ publish: id => setPublishAction({ show: true, id }) }}
      />
      <PublishDialog open={publishAction.show}
        onClose={() => setPublishAction({ show: false, id: undefined })}
        goodsKind="book"
        id={publishAction.id}
      />
    </div>
  );
}

type SimpleTabsProps = {
  id?: string;
  folder?: FolderDb;
  colWidth: number;
  setColWidth: (value: number) => void;
  addBookClicked: () => void;
  memberInformationClicked: () => void;
  addMemberClicked: () => void;
  changeOwnerClicked: () => void;
  deleteFolderClicked: () => void;
  transitionStart: (id: string, order: number) => void;
  newbornId?: string;
  setNewbornId: (id?: string) => void;
}
const SimpleTabs = (props: SimpleTabsProps) => {
  const dispatch = useDispatch();
  const folder = useOneFolderDb(props.id);
  const relationFolderBookDb = useSelector((state: RootState) => state.relationFolderBookDb);
  const relationFolderBooks = relationFolderBookDb.relationFolderBooks;
  const folderDb = useSelector((state: RootState) => state.folderDb);
  const bookDb = useSelector((state: RootState) => state.bookDb);
  const userId = useCurrentUserId();
  const [folderIds] = useAllFoldersDb(userId);
  const currentFolderId = useCurrentFolderId()
  const [bookListHash, setBookListHash] = useState<BookListHashType>({});
  const [draggingId, setDraggingId] = useState<string>();
  const [parentChangingId, setParentChangingId] = useState<string>();
  const [value, setValue] = useState(0);

  useEffect(() => {
    const newList = deepCopy(bookListHash);
    folderIds.forEach(id => {
      const rfbIds = Object.keys(relationFolderBooks).filter(rfbId => relationFolderBooks[rfbId].folderId === id);
      rfbIds.sort((a, b) => {
        const rfbA = relationFolderBooks[a];
        const rfbB = relationFolderBooks[b];
        return rfbA.order - rfbB.order;
      })
      const valueIds = rfbIds.map(rfbId => relationFolderBooks[rfbId].bookId);
      const values = valueIds.map((valueId, valueIx) => {
        return { ...bookDb.books[valueId], order: relationFolderBooks[rfbIds[valueIx]].order };
      });
      newList[id] = { ids: valueIds, values: values };
    })
    setBookListHash(newList);
  }, [folderDb, bookDb, relationFolderBooks, props.folder])

  if (!currentFolderId) return <></>;

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  }
  const changeName = (inputName: string) => {
    if (!props.id) return;
    dispatch(updateFolder({id: props.id, name: inputName}));
  }
  const getFolderId = (element: HTMLElement): string | undefined => {
    const elementClass = element.className;
    if (!element || typeof elementClass !== 'string') return;
    else if (elementClass.includes('folder-list-item')) return element.id;
    else if (element.parentElement) return getFolderId(element.parentElement);
    else return;
  }
  const getRfb = (folderId: string, bookId: string): [string, RelationFolderBookDb] => {
    const id = Object.keys(relationFolderBooks).filter(id => {
      const target = relationFolderBooks[id];
      return target.folderId === folderId && target.bookId === bookId;
    })[0];
    const value = relationFolderBooks[id];
    return [id, value];
  }
  const changeBookOrder = (ev: DraggableEvent, id: string) => {
    setParentChangingId(undefined);

    const target = ev.target as HTMLElement;
    const destinationId = getFolderId(target);

    const ids = bookListHash[currentFolderId].ids;
    const list = bookListHash[currentFolderId].values;

    const [relationId, relation] = getRfb(currentFolderId, id);
    if (destinationId && destinationId !== relation.folderId) {
      const destination = bookListHash[destinationId];
      if (!destination) return;
      ids.forEach(targetId => {
        const [targetRelationId, targetRelation] = getRfb(currentFolderId, targetId);
        if (targetId === id) return;
        else if (targetRelation.order > relation.order) dispatch(updateRelationFolderBook({ id: targetRelationId, order: targetRelation.order - 1 }));
      })
      dispatch(updateRelationFolderBook({ id: relationId, folderId: destinationId, order: destination.ids.length }));

      dispatch(setCurrentFolder(destinationId));
    } else {
      ids.forEach((targetId, targetIx) => {
        const value = list[targetIx];
        const [relationId, relation] = getRfb(currentFolderId, targetId);
        if (value.order === relation.order) return;
        dispatch(updateRelationFolderBook({ id: relationId, order: value.order }));
      })
    }
  }
  const setBookValues = (values: BookValueType[]) => {
    const newLists = deepCopy(bookListHash);
    newLists[currentFolderId].values = values;
    setBookListHash(newLists);
  }
  const checkParentChanging = (ev: DraggableEvent, id: string) => {
    const target = ev.target as HTMLElement;
    const [, relation] = getRfb(currentFolderId, id);
    const destinationId = getFolderId(target);
    if (destinationId && destinationId !== relation.folderId) setParentChangingId(id);
    else setParentChangingId(undefined);
  }
  const updateOrder = (bookId: string) => {
    if (!currentFolderId) return;
    const [targetId, target] = getRfb(currentFolderId, bookId);
    const ids = bookListHash[currentFolderId].ids;
    ids.forEach(id => {
      const [relationId, relation] = getRfb(currentFolderId, id);
      if (relationId === targetId) return;
      else if (relation.order > target.order) dispatch(updateRelationFolderBook({ id: relationId, order: relation.order - 1 }));
    })
  }

  return (
    <div>
      <TabsAppBar position="static" elevation={0}>
        <Tabs value={value} onChange={handleChange} variant="fullWidth" TabIndicatorProps={{ style: { backgroundColor: '#bdbdbd' } }}>
          <Tab label="ブック一覧" {...a11yProps(0)} />
          {/* <Tab label="共有" {...a11yProps(1)} /> */}
          <Tab label="フォルダ設定" {...a11yProps(1)} />
        </Tabs>
      </TabsAppBar>
      <TabPanel value={value} index={0}>
        <BooksGridList userId={userId}
          ids={bookListHash[currentFolderId] ? bookListHash[currentFolderId].ids : []}
          values={bookListHash[currentFolderId] ? bookListHash[currentFolderId].values : []}
          colWidth={props.colWidth} setColWidth={props.setColWidth}
          setValues={setBookValues}
          draggingId={draggingId}
          setDraggingId={setDraggingId}
          parentChangingId={parentChangingId}
          changeBookOrder={changeBookOrder}
          checkParentChanging={checkParentChanging}
          updateOrder={updateOrder}
          transitionStart={props.transitionStart}
          newbornId={props.newbornId} setNewbornId={props.setNewbornId}
        />
        <AddFab variant="extended" color="secondary" onClick={() => props.addBookClicked()}>
          <AddIcon />ブックを作る
        </AddFab>
      </TabPanel>
      {/* <TabPanel value={value} index={1}>
        <UsersGridList memberInformationClicked={() => props.memberInformationClicked()} />
        <AddFab onClick={() => props.addMemberClicked()}>
          <AddIcon />
        </AddFab>
      </TabPanel> */}
      <TabPanel value={value} index={1}>
        <Box p={4}>
          <TextField label="フォルダ名" variant="outlined" margin="normal" fullWidth
            value={folder?.name} onChange={(ev) => changeName(ev.currentTarget.value)} />
          {/* <ChangeOwnerBox>
            <Button color='primary' onClick={() => props.changeOwnerClicked()} size="large">このフォルダの所有者を変更する</Button>
          </ChangeOwnerBox> */}
          <ChangeFolderNameBox mt={5}>
            <Button color='primary' onClick={() => props.deleteFolderClicked()} size="large">このフォルダを削除する</Button>
          </ChangeFolderNameBox>
        </Box>
      </TabPanel>
    </div>
  )
}

type RenderBookCoverProps = {
  imageId?: string;
}
const RenderBookCover: React.FC<RenderBookCoverProps> = ({ imageId }) => {
  const dataUrl = imageId ? (useImageData(imageId) !== '' ? useImageData(imageId) : defaultBookCoverImage) : defaultBookCoverImage;

  return (
    <Box mt={3}>
      <BookImage src={dataUrl} draggable="false" />
    </Box>
  );
}

type UsersGridListProps = {
  memberInformationClicked: () => void
}
const UsersGridList = (props: UsersGridListProps) => {
  const users = [
    {name: 'ユーザ１', status: 'own'},
    {name: 'ユーザ２', status: 'join'},
    {name: 'ユーザ３', status: 'join'},
    {name: 'ユーザ４', status: 'join'},
    {name: 'ユーザ５', status: 'invite'}
  ]
  return (
    <div>
      <GridList cellHeight="auto" cols={3}>
        {users.map((user) => (
          <GridListTileCenter key={user.name} cols={1}>
            <IconButton onClick={() => props.memberInformationClicked()}>
              <PersonIconGrey />
            </IconButton>
            <Typography>
              {user.name}
            </Typography>
            <Typography>
              {user.status === 'invite' && '（招待中）' }
            </Typography>
          </GridListTileCenter>
        ))}
      </GridList>
    </div>
  );
}

type FolderProps = {
  updateFolderOrder: (id: string) => void;
}
const Folder: React.FC<FolderProps> = ({ updateFolderOrder }) => {
  type DialogActionState = { show: boolean; }
  const [addBookAction, setAddBookAction] = useState<DialogActionState>({show: false})
  const [memberInformationAction, setMemberInformationAction] = useState<DialogActionState>({show: false})
  const [removeMemberAction, setRemoveMemberAction] = useState<DialogActionState>({show: false})
  const [addMemberAction, setAddMemberAction] = useState<DialogActionState>({show: false})
  const [changeOwnerAction, setChangeOwnerAction] = useState<DialogActionState>({show: false})
  const [deleteFolderAction, setDeleteFolderAction] = useState<DialogActionState>({show: false})
  const [colWidth, setColWidth] = useState(160);
  const [newbornId, setNewbornId] = useState<string>();

  const currentFolderId = useCurrentFolderId();
  const currentFolder = useCurrentFolderDb();

  const pageScroll = (order: number) => {
    const booksGridList = document.getElementById(booksGridListId);
    if (!booksGridList) return;
    const colHeight = colWidth + 40;
    const scrollValue = colHeight * Math.trunc(order / listCols);
    console.log(scrollValue)
    setTimeout(() => {
      booksGridList.scrollTo({ top: scrollValue, behavior: 'smooth' });
    }, 10)
  }
  const transitionStart = (id: string, order: number) => {
    pageScroll(order);
    setNewbornId(id);
  }

  if (currentFolderId && currentFolder) {
    return (
      <>
        <FolderPaper square elevation={1}>
          <HedderPaper square elevation={3}>
            <Typography variant="body1" style={{marginLeft: '8px', fontWeight: 'bold'}}>
              <FolderOpenIcon style={{fontSize: 24, verticalAlign: -4, marginRight: 8}} />
              { currentFolder?.name }
            </Typography>
          </HedderPaper>
          <SimpleTabs id={currentFolderId} folder={currentFolder}
            colWidth={colWidth} setColWidth={setColWidth}
            addBookClicked={() => setAddBookAction({show: true})}
            memberInformationClicked={() => setMemberInformationAction({show: true})}
            addMemberClicked={() => setAddMemberAction({show: true})}
            changeOwnerClicked={() => setChangeOwnerAction({show: true})}
            deleteFolderClicked={() => setDeleteFolderAction({show: true})}
            transitionStart={transitionStart}
            newbornId={newbornId} setNewbornId={setNewbornId}
          />
        </FolderPaper>
        <AddBookDialog open={addBookAction.show} onClose={() => setAddBookAction({show: false})} transitionStart={transitionStart} />
        <MemberInformationDialog open={memberInformationAction.show} onClose={() => setMemberInformationAction({show: false})}
                                  removeMemberClicked={() => setRemoveMemberAction({show: true})} />
        <RemoveMemberDialog open={removeMemberAction.show} onClose={() => setRemoveMemberAction({show: false})} />
        <AddMemberDialog open={addMemberAction.show} onClose={() => setAddMemberAction({show: false})} />
        <ChangeOwnerDialog open={changeOwnerAction.show} onClose={() => setChangeOwnerAction({show: false})} />
        <DeleteFolderDialog open={deleteFolderAction.show}
          onClose={() => setDeleteFolderAction({show: false})}
          updateOrder={updateFolderOrder}
        />
      </>
    );
  } else {
    return (<FolderPaper square elevation={1}></FolderPaper>);
  }
}

export default Folder;
